import { keepPreviousData } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import styled from 'styled-components';
import {
  DirectoryHeader,
  Main,
  Padding,
  Stack,
  TotalEntries,
  useDirectory,
  DataObjectGroupFilterKey,
  useDataObjectGroupsQuery,
  EMPTY_PAGE,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DataObjectGroupDirectory.translations.json';
import { FooterPagination } from '../classifier-mapping/ClassifierMappingDirectory.styles';
import { Flex } from 'antd';
import { DataObjectGroupDirectoryTable } from './DataObjectGroupDirectoryTable';
import { Helmet } from 'react-helmet-async';

const FILTER_KEYS: DataObjectGroupFilterKey[] = [
  'code',
  'name',
  'groupType',
  'description',
];

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-object-group-directory',
);
addTranslations(translations);

export const DataObjectGroupDirectory: FC = () => {
  const { t } = useTranslation();

  const {
    headerProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialSort: [{ id: 'code', desc: false }],
  });

  const { data: { content, totalElements } = EMPTY_PAGE, isFetching } =
    useDataObjectGroupsQuery(
      { ...queryParameters },
      {
        placeholderData: keepPreviousData,
      },
    );

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <Main>
        <Main.Col>
          <Main.Content padding={false} id="data-object-group-content">
            <ContentWrapper>
              <DirectoryHeader
                title={t('title')}
                infoIconUrl={InfoIconUrl.DATA_OBJECT_GROUP_DIRECTORY}
                {...headerProps}
                onSearch={undefined}
              />
              <Padding bottom horizontal>
                <Flex vertical gap="small">
                  <DataObjectGroupDirectoryTable
                    data={content || []}
                    isLoading={isFetching}
                    {...tableProps}
                  />
                </Flex>
              </Padding>
            </ContentWrapper>
          </Main.Content>
          <Main.Footer>
            {!isFetching && <TotalEntries count={totalElements} />}
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
      </Main>
    </>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
